import React, {useState, useEffect, useMemo, useRef} from 'react'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import {Colors, TextFieldBorders} from './../../assets/styles'
import {makeStyles} from '@mui/styles'
import {useDispatch, useSelector} from 'react-redux'
import Constant from '../Messages/MessageConstant'
import MultilineTextInput from '../MultilineTextInput'
import {matchPath, useLocation} from 'react-router-dom'
import Routes from '../../constants/RouteConstants'
import {saveDraftMessage} from '../../actions/messagesActions'
import RXRButton from '../RXRButton'
import FileHelper from '../../lib/FileHelper'
import RXRIcon from '../RXRIcon'
import {spaceExtraExtraSmall, spaceSmall, spaceMedium, spaceExtraSmall} from '../../assets/styles/spacing'
import {
  saveMessage,
  createImageMessage,
  createTextMessage,
  loadScheduledMessagesByConversation,
  createScheduledMessage,
  updateScheduledMessage,
  deleteScheduledMessage,
} from './chatContainerQueries'
import {getAuthedUserFromChatRoom} from '../../Utils/chatUtils'
import PropTypes from 'prop-types'
import {selectAuthedUserId} from '../../reducers/selectors'
import DateInput from '../DateInput'
import SelectInput from '../SelectInput'
import {formatScheduledTime, generateTimeSlots} from '../../Utils/scheduledMessageUtils'

const useStyles = makeStyles(theme => ({
  fullContainer: {
    width: '100%',
  },
  schedulingContainer: {
    backgroundColor: Colors.rxrTint15Color,
    padding: spaceMedium,
    borderTop: `1px solid ${Colors.rxrGreyColor}`,
    borderLeft: `1px solid ${Colors.rxrGreyColor}`,
    borderRight: `1px solid ${Colors.rxrGreyColor}`,
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    position: 'relative',
  },
  schedulingHeader: {
    fontSize: '14px',
    fontWeight: 600,
    color: Colors.rxrPrimaryColor,
    marginBottom: spaceSmall,
    display: 'flex',
    alignItems: 'center',
    gap: spaceSmall,
  },
  dateTimeInputWrapper: {
    width: '100%',
    marginBottom: spaceSmall,
    paddingRight: '48px', // Make space for the cancel button
    display: 'flex',
    alignItems: 'flex-start',
    gap: spaceSmall,
  },
  clockIconWrapper: {
    paddingTop: '8px', // Align with the date picker label
    flexShrink: 0,
  },
  inputsWrapper: {
    display: 'flex',
    gap: spaceSmall,
    flex: 1,
    flexWrap: 'wrap',
  },
  dateInput: {
    width: '180px',
    flexShrink: 0,
  },
  timeInput: {
    width: '140px',
    flexShrink: 0,
  },
  cancelButton: {
    position: 'absolute',
    top: spaceSmall,
    right: spaceSmall,
  },
  attachmentPreview: {
    display: 'flex',
    gap: spaceExtraSmall,
    padding: spaceSmall,
    backgroundColor: Colors.rxrWhiteColor,
    borderRadius: '4px',
    marginBottom: spaceSmall,
    border: `1px solid ${Colors.rxrGreyColor}`,
  },
  attachmentName: {
    flex: 1,
    fontSize: '14px',
    color: Colors.rxrPrimaryColor,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  removeAttachmentButton: {
    padding: '4px',
  },
  attachmentHelperText: {
    fontSize: '12px',
    color: Colors.rxrDarkGreyColor,
    marginBottom: spaceSmall,
  },
  inputContainer: props => ({
    ...Colors.backgroundLightGrey,
    display: 'flex',
    padding: props.isMobile ? `${spaceExtraSmall}px` : `${spaceSmall}px ${spaceMedium}px`,
    width: '100%',
    alignItems: 'center',
  }),
  inputContainerScheduling: props => ({
    borderTop: 'none',
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
    borderBottom: `1px solid ${Colors.rxrGreyColor}`,
    borderLeft: `1px solid ${Colors.rxrGreyColor}`,
    borderRight: `1px solid ${Colors.rxrGreyColor}`,
  }),
  sendButton: {},
  scheduleButton: props => ({
    width: props.isMobile ? 42 : 56,
    height: props.isMobile ? 42 : 56,
    margin: spaceExtraExtraSmall,
  }),
  uploadButton: props => ({
    width: props.isMobile ? 42 : 56,
    height: props.isMobile ? 42 : 56,
    margin: spaceExtraExtraSmall,
  }),
  messageTextField: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: Colors.rxrWhiteColor,
      '& fieldset': {
        ...TextFieldBorders.messageInputBorder,
      },
      '&:hover fieldset': {
        ...TextFieldBorders.messageInputBorder,
      },
      '&.Mui-disabled fieldset': {
        ...TextFieldBorders.messageInputBorder,
      },
      '&.Mui-focused fieldset': {
        ...TextFieldBorders.messageInputBorder,
      },
    },
    '& .MuiOutlinedInput-input': {
      color: Colors.rxrDarkGreyColor,
    },
  },
}))

function ChatContainerInput(props) {
  const isMobile = useSelector(state => state.App.isMobile)
  const classes = useStyles({isMobile})
  const fileInputRef = useRef(null)
  const [messageInput, setMessageInput] = useState('')
  const [viewingConversationId, setViewingConversationId] = useState(null)
  const [isSchedulingMode, setIsSchedulingMode] = useState(false)
  const [scheduledDate, setScheduledDate] = useState(null)
  const [scheduledTime, setScheduledTime] = useState('')
  const [schedulingError, setSchedulingError] = useState('')
  const [editingScheduledMessage, setEditingScheduledMessage] = useState(null)
  const [scheduledAttachment, setScheduledAttachment] = useState(null)
  const currentLocation = useLocation()

  const conversationModel = useSelector(state => state.Messages.conversationsLookup[viewingConversationId])
  const authedStaffId = useSelector(selectAuthedUserId)
  const activeBuilding = useSelector(state => state.Buildings.buildingsLookup[state.Buildings.activeBuildingId])
  const groupPermissions = useSelector(state => state.GroupPermissions.permissionsObject)
  const draftMessagesLookup = useSelector(state => state.Messages.draftMessagesByConversationId)
  const staffLookup = useSelector(state => state.Staff.staffLookup)

  // Scheduled messages state - loaded from GraphQL
  const [scheduledMessages, setScheduledMessages] = useState([])
  const [scheduledMessagesLoading, setScheduledMessagesLoading] = useState(false)

  const dispatch = useDispatch()

  const authedChatUser = getAuthedUserFromChatRoom(conversationModel, authedStaffId, staffLookup)

  useEffect(() => {
    const viewConversationMatch = matchPath(currentLocation.pathname, {path: Routes.MESSAGES_VIEW_SINGLE})

    if (!viewConversationMatch) {
      // this shouldn't happen since we only render if we're viewing a conversation
      return
    }

    const newConversationId = viewConversationMatch.params.conversationId
    if (newConversationId === viewingConversationId) {
      // if the conversation hasn't changed, we skip
      return
    }

    setViewingConversationId(newConversationId)

    if (viewConversationMatch.state && viewConversationMatch.state.prefillMessage) {
      setMessageInput(viewConversationMatch.state.prefillMessage)
      // also immediately put this to our draft state
      saveDraftMessage(dispatch, newConversationId, viewConversationMatch.state.prefillMessage)
    } else if (draftMessagesLookup[newConversationId]) {
      setMessageInput(draftMessagesLookup[newConversationId])
    } else {
      setMessageInput('')
    }
  }, [currentLocation, viewingConversationId, draftMessagesLookup])

  // Load scheduled messages when conversation changes
  useEffect(() => {
    const loadScheduledMessages = async () => {
      if (!viewingConversationId || conversationModel?.isOnboardingChat) {
        // Don't load scheduled messages for onboarding chats
        setScheduledMessages([])
        return
      }

      setScheduledMessagesLoading(true)
      try {
        const scheduledMsgs = await loadScheduledMessagesByConversation(viewingConversationId)
        setScheduledMessages(scheduledMsgs)
      } catch (error) {
        console.error('Failed to load scheduled messages:', error)
        // Fallback to empty array on error
        setScheduledMessages([])
      } finally {
        setScheduledMessagesLoading(false)
      }
    }

    loadScheduledMessages()
  }, [viewingConversationId, conversationModel?.isOnboardingChat])

  const handleChangeInput = newVal => {
    saveDraftMessage(dispatch, viewingConversationId, newVal)
    setMessageInput(newVal)
  }

  // still only supporting one image at a time
  const sendImage = async imageData => {
    const message = await createImageMessage(conversationModel, authedChatUser, imageData)
    props.onMessageSending(message)
    const savedMessage = await saveMessage(dispatch, conversationModel, message)
    props.onMessageSent(savedMessage)
  }

  const sendText = async () => {
    if (messageInput.trim().length === 0) {
      return
    }

    const message = await createTextMessage(conversationModel, authedChatUser, messageInput)
    props.onMessageSending(message)
    handleChangeInput('')
    try {
      const savedMessage = await saveMessage(dispatch, conversationModel, message)
      props.onMessageSent(savedMessage)
    } catch (err) {
      console.error(err)
      window.alert(err.message)
      // put our input back how it was
      handleChangeInput(messageInput)
    }
  }

  const handleImage = e => {
    FileHelper.Instance()
      .handleSelectFilesInputEventUploadToS3(e, FileHelper.IMAGE_FILE_TYPES)
      .then(s3Object => {
        if (isSchedulingMode) {
          // Store attachment for scheduled message instead of sending immediately
          setScheduledAttachment(s3Object[0])
          // Clear message input when adding attachment (either/or behavior)
          setMessageInput('')
        } else {
          return sendImage(s3Object[0])
        }
      })
      .catch(err => {
        console.error(err)
        window.alert(err.message)
      })
  }

  const handleRemoveAttachment = () => {
    setScheduledAttachment(null)
    // Reset file input to allow re-uploading the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const timeSlotOptions = generateTimeSlots(5) // 5-minute intervals

  const validateScheduling = () => {
    if (!messageInput.trim() && !scheduledAttachment) {
      setSchedulingError('Message cannot be empty')
      return false
    }

    if (!scheduledDate) {
      setSchedulingError('Please select a date')
      return false
    }

    if (!scheduledTime) {
      setSchedulingError('Please select a time')
      return false
    }

    // Combine date and time
    const selectedDateTime = new Date(scheduledDate)
    const timeObj = new Date(scheduledTime)
    selectedDateTime.setHours(timeObj.getHours(), timeObj.getMinutes(), 0, 0)

    const now = new Date()
    if (selectedDateTime <= now) {
      setSchedulingError('Scheduled time must be in the future')
      return false
    }

    setSchedulingError('')
    return true
  }

  const handleEnterSchedulingMode = () => {
    setIsSchedulingMode(true)
    setScheduledDate(null)
    setScheduledTime('')
    setSchedulingError('')
  }

  const handleCancelScheduling = () => {
    resetSchedulingForm()
  }

  // Helper function to reset scheduling form
  const resetSchedulingForm = () => {
    setIsSchedulingMode(false)
    setScheduledDate(null)
    setScheduledTime('')
    setSchedulingError('')
    setMessageInput('')
    setEditingScheduledMessage(null)
    setScheduledAttachment(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Helper function to combine date and time
  const combineDateTime = (date, time) => {
    const selectedDateTime = new Date(date)
    const timeObj = new Date(time)
    selectedDateTime.setHours(timeObj.getHours(), timeObj.getMinutes(), 0, 0)
    return selectedDateTime
  }

  // Helper function to find matching time slot
  const findMatchingTimeSlot = dateTime => {
    return timeSlotOptions.find(slot => {
      const slotTime = new Date(slot.value)
      return slotTime.getHours() === dateTime.getHours() && slotTime.getMinutes() === dateTime.getMinutes()
    })
  }

  // Helper function to populate form for editing
  const populateFormForEdit = (originalMessage, displayMessage) => {
    setEditingScheduledMessage(originalMessage)

    const messageText = displayMessage.content || originalMessage.body || ''
    setMessageInput(messageText)

    const scheduledDateTime = new Date(originalMessage.scheduledSendAt)
    setScheduledDate(scheduledDateTime)

    const timeSlot = findMatchingTimeSlot(scheduledDateTime)
    setScheduledTime(timeSlot ? timeSlot.value : '')
    setScheduledAttachment(originalMessage.image || null)
    setSchedulingError('')
    setIsSchedulingMode(true)
  }

  // Main function to save scheduled message (handles both create and update)
  const handleSaveScheduledMessage = async () => {
    if (!validateScheduling()) {
      return
    }

    const scheduledDateTime = combineDateTime(scheduledDate, scheduledTime)

    try {
      if (editingScheduledMessage) {
        // Update existing scheduled message
        const updateInput = {
          id: editingScheduledMessage.id,
          body: messageInput,
          scheduledSendAt: scheduledDateTime.toISOString(),
        }

        // Handle image update: include image field if attachment exists, or explicitly set to null to remove
        if (scheduledAttachment) {
          updateInput.image = FileHelper.formatS3ObjectForInput(scheduledAttachment)
          updateInput.body = ''
        } else {
          updateInput.image = null
        }

        const updatedMessage = await updateScheduledMessage(updateInput)

        // Update local state
        setScheduledMessages(prev => prev.map(msg => (msg.id === editingScheduledMessage.id ? updatedMessage : msg)))
      } else {
        // Get recipient from conversation participants
        const residentUser = conversationModel?.chatUsers?.find(user => !user.isStaffUser)
        if (!residentUser) {
          throw new Error('Could not find recipient for scheduled message')
        }

        // Create new scheduled message
        const createInput = {
          scheduledSendAt: scheduledDateTime.toISOString(),
          conversationId: viewingConversationId,
          staffId: authedStaffId,
          recipient: residentUser.userId,
          body: messageInput,
          type: 'CONVERSATION',
          buildingId: activeBuilding?.id,
          buildingTimezone: activeBuilding?.timezone || 'America/New_York',
        }
        // Add attachment if present
        if (scheduledAttachment) {
          createInput.image = FileHelper.formatS3ObjectForInput(scheduledAttachment)
        }

        const newMessage = await createScheduledMessage(createInput)

        // Update local state
        setScheduledMessages(prev => [...prev, newMessage])
      }

      resetSchedulingForm()
    } catch (err) {
      console.error('Error scheduling message:', err)
      setSchedulingError(err.message || 'Failed to schedule message')
    }
  }

  // Function to initiate editing of a scheduled message
  const handleEditScheduledMessage = scheduledMessage => {
    const originalScheduledMessage = scheduledMessages.find(msg => msg.id === scheduledMessage.id)

    if (!originalScheduledMessage) {
      console.error('Could not find original scheduled message with ID:', scheduledMessage.id)
      return
    }

    populateFormForEdit(originalScheduledMessage, scheduledMessage)
  }

  const handleCancelScheduledMessage = async scheduledMessageId => {
    try {
      // Delete from server
      await deleteScheduledMessage(scheduledMessageId)

      // Update local state
      setScheduledMessages(prev => prev.filter(msg => msg.id !== scheduledMessageId))
    } catch (error) {
      console.error('Error deleting scheduled message:', error)
      // Optionally show an error message to the user
      window.alert('Failed to delete scheduled message. Please try again.')
    }
  }

  const formatScheduledTimeForChat = scheduledSendAt => {
    return formatScheduledTime(scheduledSendAt, activeBuilding?.timezone, {includeWeekday: true})
  }

  // Set minimum date to current time + 1 minute
  const minDateTime = new Date()
  minDateTime.setMinutes(minDateTime.getMinutes() + 1)

  // Filter scheduled messages for current conversation
  const currentConversationScheduledMessages = useMemo(() => {
    return scheduledMessages.filter(msg => msg.conversationId === viewingConversationId)
  }, [scheduledMessages, viewingConversationId])

  // Expose scheduled messages to parent component via callback
  React.useEffect(() => {
    if (props.onScheduledMessagesChange) {
      props.onScheduledMessagesChange(currentConversationScheduledMessages, {
        onEdit: handleEditScheduledMessage,
        onCancel: handleCancelScheduledMessage,
        formatTime: formatScheduledTimeForChat,
      })
    }
  }, [currentConversationScheduledMessages, props.onScheduledMessagesChange])

  return (
    <div className={classes.fullContainer}>
      {/* Scheduling interface - shown when in scheduling mode */}
      {isSchedulingMode && (
        <div className={classes.schedulingContainer}>
          <div className={classes.schedulingHeader}>{editingScheduledMessage ? 'Edit Scheduled Message' : 'Schedule Message'}</div>

          {/* Cancel scheduling button - positioned in top right */}
          <IconButton aria-label="cancel scheduling" onClick={handleCancelScheduling} className={classes.cancelButton} size="large">
            <RXRIcon
              icon={RXRIcon.CLOSE}
              color={activeBuilding.outOfOfficeStatus && groupPermissions.messagesWrite ? Colors.rxrPrimaryColor : Colors.rxrGreyColor}
              size={RXRIcon.SIZE_LARGE}
            />
          </IconButton>

          <div className={classes.dateTimeInputWrapper}>
            <div className={classes.clockIconWrapper}>
              <RXRIcon icon={RXRIcon.CLOCK} size={RXRIcon.SIZE_LARGE} color={Colors.rxrPrimaryColor} />
            </div>
            <div className={classes.inputsWrapper}>
              <div className={classes.dateInput}>
                <DateInput
                  label="Date"
                  value={scheduledDate}
                  onChange={setScheduledDate}
                  min={minDateTime}
                  isRequired={true}
                  helperText={`Building timezone: ${activeBuilding?.timezone || 'America/New_York'}`}
                />
              </div>
              <div className={classes.timeInput}>
                <SelectInput label="Time" value={scheduledTime} onChange={setScheduledTime} options={timeSlotOptions} isRequired={true} />
              </div>
            </div>
          </div>

          {/* Attachment preview */}
          {scheduledAttachment && (
            <>
              <div className={classes.attachmentPreview}>
                <RXRIcon icon={RXRIcon.FILE_IMAGE} color={Colors.rxrPrimaryColor} size={RXRIcon.SIZE_MEDIUM} />
                <div className={classes.attachmentName}>{scheduledAttachment.key?.split('/').pop() || 'Image attachment'}</div>
                <IconButton onClick={handleRemoveAttachment} className={classes.removeAttachmentButton} size="small">
                  <RXRIcon icon={RXRIcon.CLOSE} color={Colors.rxrPrimaryColor} size={RXRIcon.SIZE_SMALL} />
                </IconButton>
              </div>
              <div className={classes.attachmentHelperText}>Image attached. Remove the attachment to send a text message instead.</div>
            </>
          )}

          {schedulingError && (
            <Typography color="error" variant="body2" style={{fontSize: '12px', marginBottom: spaceSmall}}>
              {schedulingError}
            </Typography>
          )}
        </div>
      )}

      {/* Message input container */}
      <div className={`${classes.inputContainer} ${isSchedulingMode ? classes.inputContainerScheduling : ''}`}>
        <MultilineTextInput
          id="outlined-multiline-flexible"
          rowsMax={4}
          placeholder={isSchedulingMode ? 'Type your scheduled message...' : 'Type a message'}
          value={messageInput}
          onChange={handleChangeInput}
          className={classes.messageTextField}
          isDisabled={!activeBuilding.outOfOfficeStatus || !groupPermissions.messagesWrite || (isSchedulingMode && scheduledAttachment)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              if (isSchedulingMode) {
                handleSaveScheduledMessage()
              } else {
                sendText().then()
              }
            }
          }}
        />

        {/* Schedule message button - HIDDEN for onboarding chats */}
        {!isSchedulingMode && !conversationModel?.isOnboardingChat && (
          <IconButton
            aria-label="schedule message"
            onClick={handleEnterSchedulingMode}
            disabled={!activeBuilding.outOfOfficeStatus || !groupPermissions.messagesWrite}
            className={classes.scheduleButton}
            size="large"
          >
            <RXRIcon
              icon={RXRIcon.CLOCK}
              color={activeBuilding.outOfOfficeStatus && groupPermissions.messagesWrite ? Colors.rxrPrimaryColor : Colors.rxrGreyColor}
              size={RXRIcon.SIZE_EXTRA_LARGE}
            />
          </IconButton>
        )}

        <input
          ref={fileInputRef}
          name="image"
          onChange={handleImage}
          accept="image/*"
          style={{display: 'none'}}
          id="raised-button-file"
          multiple
          type="file"
          disabled={!activeBuilding.outOfOfficeStatus || !groupPermissions.messagesWrite}
        />
        <label htmlFor="raised-button-file">
          <IconButton
            aria-label="upload picture"
            component="span"
            disabled={!groupPermissions.messagesWrite}
            className={classes.uploadButton}
            size="large"
          >
            <RXRIcon
              icon={RXRIcon.FILE_IMAGE}
              color={activeBuilding.outOfOfficeStatus && groupPermissions.messagesWrite ? Colors.rxrPrimaryColor : Colors.rxrGreyColor}
              size={RXRIcon.SIZE_EXTRA_LARGE}
            />
          </IconButton>
        </label>
        <RXRButton
          onClick={isSchedulingMode ? handleSaveScheduledMessage : () => sendText()}
          className={classes.sendButton}
          disabled={
            !activeBuilding.outOfOfficeStatus || !groupPermissions.messagesWrite || (!isSchedulingMode && messageInput.trim() === '')
          }
        >
          {isSchedulingMode ? (editingScheduledMessage ? 'Save' : 'Save') : Constant.MESSAGE_SEND_CONSTANT}
        </RXRButton>
      </div>
    </div>
  )
}

ChatContainerInput.propTypes = {
  onMessageSending: PropTypes.func.isRequired,
  onMessageSent: PropTypes.func.isRequired,
  onScheduledMessagesChange: PropTypes.func, // Optional callback to pass scheduled messages to parent for rendering
}

export default ChatContainerInput
